import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import './LanguageSwitcher.css';

const LanguageSwitcher = ({ compact = false }) => {
  const { setSettings } = useAuth();
  const { t, languageName, changeLanguage, getAvailableLanguages,
          isLoadingLang, currentLanguage: currentLangCode } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [isApiLoading, setIsApiLoading] = useState(false);

  const availableLanguages = getAvailableLanguages();

  const handleLanguageChange = async (e, languageObj) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    try {
      // Backend-Save fire & forget — Promise.resolve() absichern falls setSettings kein Promise zurückgibt
      if (setSettings && languageObj.id) {
        Promise.resolve(setSettings({ language: languageObj.id })).catch(err =>
          console.error('Backend save failed:', err)
        );
      }
      // UI sofort umschalten
      await changeLanguage(languageObj.code || languageObj.name);
      setIsOpen(false);
    } catch (error) {
      console.error('Failed to change language:', error);
    }
  };

  // Aktive Sprache: per Code (zuverlässig) oder Name
  const currentLanguage = availableLanguages.find(lang =>
    lang.code === currentLangCode || lang.name === languageName
  );

  const getLanguageFlagFromName = (url) => {
    if (!url) return '🌐';
    const flagMap = {
      '/de.png': '🇩🇪', '/en.png': '🇺🇸', '/gb.png': '🇺🇸',
      '/nl.png': '🇳🇱', '/fr.png': '🇫🇷', '/ru.png': '🇷🇺',
      '/pl.png': '🇵🇱', '/ar.png': '🇸🇦', '/es.png': '🇪🇸',
      '/ro.png': '🇷🇴', '/tr.png': '🇹🇷', '/hr.png': '🇭🇷',
      '/it.png': '🇮🇹', '/sq.png': '🇦🇱', '/bg.png': '🇧🇬',
      '/uk.png': '🇺🇦', '/cs.png': '🇨🇿', '/el.png': '🇬🇷',
    };
    for (const [suffix, emoji] of Object.entries(flagMap)) {
      if (url.includes(suffix)) return emoji;
    }
    return '🌐';
  };

  const renderFlag = (flag) => {
    if (flag && flag.startsWith('http')) {
      return (
        <span className="language-flag">
          <img
            src={flag}
            alt="flag"
            className="language-flag-image"
            onError={(e) => {
              e.target.style.display = 'none';
              const fallback = e.target.parentElement.querySelector('.language-flag-emoji');
              if (fallback) fallback.style.display = 'inline-block';
            }}
          />
          <span className="language-flag-emoji" style={{ display: 'none' }}>
            {getLanguageFlagFromName(flag) || '🌐'}
          </span>
        </span>
      );
    }
    return (
      <span className="language-flag">
        <span className="language-flag-emoji">{flag || '🌐'}</span>
      </span>
    );
  };

  if (compact) {
    return (
      <div className="language-switcher-compact">
        <button
          className="language-button-compact"
          onClick={() => setIsOpen(!isOpen)}
          title={t('language')}
          disabled={isLoadingLang || isApiLoading}
        >
          {isLoadingLang || isApiLoading ? (
            <div className="language-loading-spinner"></div>
          ) : (
            renderFlag(currentLanguage?.flag)
          )}
        </button>

        {isOpen && (
          <div className="language-dropdown-compact">
            {availableLanguages.map((language) => (
              <button
                key={language.id || language.code}
                className={`language-option-compact ${language.name === languageName ? 'active' : ''}`}
                onClick={(e) => handleLanguageChange(e, language)}
                disabled={isLoadingLang || isApiLoading}
              >
                {renderFlag(language.flag)}
                <span className="language-name">{language.name}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="language-switcher">
      <button
        className="language-button"
        onClick={() => setIsOpen(!isOpen)}
        disabled={isLoadingLang || isApiLoading}
      >
        {isLoadingLang || isApiLoading ? (
          <div className="language-loading-spinner"></div>
        ) : (
          <>
            {renderFlag(currentLanguage?.flag)}
            <span className="language-name">
              {currentLanguage?.name || 'Deutsch'}
            </span>
          </>
        )}
        <svg
          className={`dropdown-arrow ${isOpen ? 'open' : ''}`}
          width="12"
          height="8"
          viewBox="0 0 12 8"
          fill="none"
        >
          <path d="M1 1L6 6L11 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {isOpen && (
        <div className="language-dropdown">
          {availableLanguages.map((language) => (
            <button
              key={language.id || language.code}
              className={`language-option ${language.name === languageName ? 'active' : ''}`}
              onClick={(e) => handleLanguageChange(e, language)}
              disabled={isLoadingLang || isApiLoading}
            >
              {renderFlag(language.flag)}
              <span className="language-name">{language.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
